/* eslint react-hooks/exhaustive-deps: "warn" */

import { type CSSProperties, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './SchemaSection.module.scss';

type Translate = (key: string) => string;

type LevelKey = 'ip' | 'du' | 'gu';

type Level = {
  key: LevelKey;
  title: string;
  blurb: string;
  modeText: string;
  resLabel: string;
  resName: string;
  resId: string;
  resMeta: string;
};

// Stable ordering/keys for scroll-sync logic (independent of translated copy).
const LEVEL_KEYS: LevelKey[] = ['ip', 'du', 'gu'];

const getLevels = (t: Translate): Level[] => [
  {
    key: 'ip',
    title: t('Immediate Parent'),
    blurb: t('The entity directly above the subject company — direct control and ownership line.'),
    modeText: t('Immediate Parent — the entity directly above your record'),
    resLabel: t('Immediate Parent'),
    resName: 'ACME EUROPE HOLDCO LTD',
    resId: 'GDB000222333',
    resMeta: '20 Bishopsgate, London · 🇬🇧 GB',
  },
  {
    key: 'du',
    title: t('Domestic Ultimate'),
    blurb: t('The highest entity in the same country — in-country consolidation, tax grouping, national exposure.'),
    modeText: t('Domestic Ultimate — the highest entity in the same country'),
    resLabel: t('Domestic Ultimate'),
    resName: 'ACME HOLDINGS UK LTD',
    resId: 'GDB000987654',
    resMeta: '10 Finsbury Sq, London · 🇬🇧 GB',
  },
  {
    key: 'gu',
    title: t('Global Ultimate'),
    blurb: t('The highest entity worldwide — the anchor every subsidiary in the group rolls up to.'),
    modeText: t('Global Ultimate — the anchor the whole group rolls up to'),
    resLabel: t('Global Ultimate'),
    resName: 'ACME GROUP PLC',
    resId: 'GDB000123999',
    resMeta: '1 Market St, Dublin · 🇮🇪 IE',
  },
];

const MOBILE_QUERY = '(max-width: 1023px)';

export const SchemaSection = () => {
  const { t } = useInt();
  const levels = getLevels(t);
  const [activeKey, setActiveKey] = useState<LevelKey>('ip');
  const activeIndex = levels.findIndex((level) => level.key === activeKey);
  const active = levels[activeIndex];

  const tallRef = useRef<HTMLDivElement>(null);
  const curKeyRef = useRef<LevelKey>('ip');
  const gridRef = useRef<HTMLDivElement>(null);
  const gridRevealed = useIntersection(gridRef, { threshold: 0.15 });
  const cardRef = useRef<HTMLDivElement>(null);

  const handleCardMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;

    if (!card || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const rect = card.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    const maxTilt = 3;

    card.style.transform = `perspective(1200px) rotateX(${(-py * maxTilt).toFixed(2)}deg) rotateY(${(px * maxTilt).toFixed(2)}deg) translateY(-3px)`;
    card.style.setProperty('--glareX', `${(px + 0.5) * 100}%`);
    card.style.setProperty('--glareY', `${(py + 0.5) * 100}%`);
  };

  const handleCardMouseLeave = () => {
    const card = cardRef.current;

    if (!card) {
      return;
    }

    card.style.transform = '';
  };

  useEffect(() => {
    const tall = tallRef.current;

    if (!tall) {
      return undefined;
    }

    let ticking = false;

    const syncToScroll = () => {
      if (window.matchMedia(MOBILE_QUERY).matches) {
        return;
      }

      const range = tall.offsetHeight - window.innerHeight;

      if (range <= 0) {
        return;
      }

      const y = -tall.getBoundingClientRect().top;
      const progress = Math.max(0, Math.min(1, y / range));
      const index = Math.min(2, Math.floor(progress * 3));
      const key = LEVEL_KEYS[index];

      if (key !== curKeyRef.current) {
        curKeyRef.current = key;
        setActiveKey(key);
      }
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          syncToScroll();
          ticking = false;
        });
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const gotoMode = (key: LevelKey) => {
    const tall = tallRef.current;
    const isMobile = window.matchMedia(MOBILE_QUERY).matches;

    if (tall && !isMobile) {
      const index = LEVEL_KEYS.findIndex((k) => k === key);
      const range = tall.offsetHeight - window.innerHeight;
      const top = tall.getBoundingClientRect().top + window.pageYOffset;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.scrollTo({
        top: top + range * ((index + 0.5) / 3),
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    }

    curKeyRef.current = key;
    setActiveKey(key);
  };

  return (
    <Section background="#f2f6fd" className={styles.root}>
      <div className={styles.tall} ref={tallRef}>
        <div className={styles.sticky}>
          <div className={styles.grid} ref={gridRef}>
            <div className={cn(styles.copy, gridRevealed && styles.copyIn)}>
              <TitleBlock
                align="left"
                title={t('Three resolved levels, 24 flat fields')}
                description={t(
                  'Every company record carries its full position in the group — each level resolved to a persistent ID, legal name, complete address, and ISO country code. Click a level to see it.',
                )}
              />

              <div className={styles.levelList}>
                {levels.map((level, i) => (
                  <button
                    key={level.key}
                    type="button"
                    className={cn(styles.levelBtn, level.key === activeKey && styles.levelBtnActive)}
                    onClick={() => gotoMode(level.key)}
                  >
                    <span className={styles.levelNum}>{String(i + 1).padStart(2, '0')}</span>
                    <span>
                      <b>{level.title}</b>
                      <small>{level.blurb}</small>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div
              className={cn(styles.card, gridRevealed && styles.cardIn)}
              ref={cardRef}
              onMouseMove={handleCardMouseMove}
              onMouseLeave={handleCardMouseLeave}
            >
              <div className={styles.cardGlare} aria-hidden="true" />

              <div className={styles.cardHead}>
                <span className={styles.cardHeadLabel}>{t('Sample resolution')}</span>
                <span className={styles.cardHeadMeta}>
                  {`1 ${t('lookup')} · ${activeIndex + 1} ${activeIndex ? t('levels') : t('level')}`}
                </span>
              </div>

              <div className={styles.mode}>{active.modeText}</div>

              <div className={styles.body}>
                <div className={styles.subject}>
                  <span className={styles.subjectDot} />
                  <div>
                    <div className={styles.rowLabel}>{t('Your record')}</div>
                    <div className={styles.rowName}>ACME PAYMENTS LTD</div>
                    <div className={styles.rowMetaSm}>London · 🇬🇧 GB</div>
                  </div>
                </div>

                {levels.map((level, i) => (
                  <div key={level.key} className={styles.linkGroup} style={{ '--i': i } as CSSProperties}>
                    <div className={cn(styles.connector, i <= activeIndex && styles.connectorActive)}>
                      {t('owned by')}
                    </div>

                    <button
                      type="button"
                      onClick={() => gotoMode(level.key)}
                      className={cn(
                        styles.row,
                        level.key === activeKey && styles.rowActive,
                        i > activeIndex && styles.rowDimmed,
                      )}
                    >
                      <span className={styles.rowNum}>{i + 1}</span>

                      <div className={styles.rowMain}>
                        <div className={styles.rowTop}>
                          <span className={styles.rowLabel}>{level.resLabel}</span>
                          <span className={styles.rowId}>{level.resId}</span>
                        </div>
                        <div className={styles.rowName}>{level.resName}</div>
                        <div className={styles.rowMeta}>{level.resMeta}</div>
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
